/**
 * RC8 Phase 1 · Slice 6 · Offline Foundation · Freshness / Eviction Policy.
 *
 * Encodes ADR-MOBILE-003 §3.2 into runtime helpers so the storage
 * layer never invents a policy. Every consumer reads from here — if
 * the ADR changes, this file changes, and every store follows.
 *
 * Class summary (authoritative table in ADR-MOBILE-003 §3.1):
 *   · C1 Public Editorial    — manifest supersession + LRU; voyage
 *                              archived > 30 d → evict.
 *   · C2 Personal Travel     — voyage archived (past disembark + 30 d)
 *                              → soft-evict; voyage deleted server-side
 *                              → hard-evict immediately.
 *   · C3 Sensitive Identity  — evict immediately on session revoke or
 *                              voyage archive.
 *   · C4 Operational Live    — TTL per field (5 min · 15 min · 1 h).
 *
 * This module NEVER invents a TTL or an eviction rule. If a class does
 * not have a documented policy, the accessor throws — the caller must
 * resolve the ambiguity by going back to the ADR, not by guessing.
 */

// ADR-MOBILE-003 §3.2.C2 — soft-evict past disembark + 30 days.
export const C2_SOFT_EVICT_DAYS_PAST_DISEMBARK = 30;

// ADR-MOBILE-003 §3.2.C1 — evict editorial for voyages archived > 30 d.
export const C1_EVICT_DAYS_PAST_ARCHIVE = 30;

// ADR-MOBILE-003 §3.2.C4 — per-field TTLs (only the ones cited in the ADR).
// Extend this map ONLY when the ADR gains a new C4 field with a policy.
export const C4_TTL_MINUTES = Object.freeze({
  weather: 15,
  currency: 60,
  port_status: 15,
  ship_position: 5,
});

const DAY_MS = 24 * 60 * 60 * 1000;
const MIN_MS = 60 * 1000;


/**
 * True when a C2 voyage entry should still render as authoritative.
 * @param {string|null} disembarkDateIso — voyage.disembark_date (`YYYY-MM-DD`)
 * @param {Date} [now] — for tests
 */
export function c2IsFresh(disembarkDateIso, now = new Date()) {
  if (!disembarkDateIso) return true; // pre-voyage / open-ended
  try {
    const [y, m, d] = disembarkDateIso.split("-").map(Number);
    const disembark = Date.UTC(y, m - 1, d);
    const soft = disembark + C2_SOFT_EVICT_DAYS_PAST_DISEMBARK * DAY_MS;
    return now.getTime() <= soft;
  } catch {
    // Malformed date → treat as fresh rather than fabricate a policy;
    // eviction happens through explicit server-side hard-delete.
    return true;
  }
}


/**
 * True when a cached C4 field is still within its TTL window.
 * @param {string} field — one of `Object.keys(C4_TTL_MINUTES)`
 * @param {number|string} storedAtMs — timestamp the field was cached
 * @param {Date} [now]
 */
export function c4IsFresh(field, storedAtMs, now = new Date()) {
  const ttl = C4_TTL_MINUTES[field];
  if (!ttl) {
    // No policy in the ADR for this field. Refuse to invent one.
    throw new Error(
      `RC8 Slice 6: no C4 TTL policy in ADR-MOBILE-003 for field="${field}"`,
    );
  }
  const t = Number(storedAtMs);
  if (!Number.isFinite(t)) return false;
  return (now.getTime() - t) <= ttl * MIN_MS;
}


/**
 * C3 eviction is not TTL-based. This function returns true whenever
 * the ADR says the C3 record must be evicted RIGHT NOW.
 */
export function c3ShouldEvict({ sessionRevokedAt, voyageArchived } = {}) {
  return Boolean(sessionRevokedAt) || Boolean(voyageArchived);
}


// ---------------------------------------------------------------------------
// RC7 · M4 · Pack-manifest freshness (P1 repair · 2026-02-17)
// ---------------------------------------------------------------------------
//
// `pack.js` reads a manifest's `last_server_sync_at` + `dominant_data_class`
// and needs to know whether the manifest is fresh / stale / expired. This
// helper encodes the ADR-MOBILE-003 per-class age windows uniformly.
//
// Rules (mirror ADR-MOBILE-003 §3.2):
//   C1 · Public Editorial       → fresh ≤ 24h · stale ≤ 7d · expired > 7d
//   C2 · Personal Travel        → fresh ≤ 12h · stale ≤ 72h · expired > 72h
//   C3 · Sensitive Identity     → NEVER fresh here — always report `expired`
//                                 so no consumer treats a stale sensitive
//                                 manifest as reliable. C3 has its own
//                                 biometric-gated online flow (M6).
//   Unknown class               → treat as C2 for safety.
//
// Never trust device wall-clock alone: the caller supplies
// `lastServerSyncAt` (an ISO string persisted by `sync.js` from the
// server's own response headers/timestamp) and we compare against the
// device clock ONLY to compute an age; if the clock is skewed the worst
// outcome is a false "stale" — never a false "fresh".

const FRESHNESS_WINDOWS_MS = Object.freeze({
  C1: { fresh: 24 * 60 * MIN_MS,     stale: 7  * DAY_MS },
  C2: { fresh: 12 * 60 * MIN_MS,     stale: 72 * 60 * MIN_MS },
});

/**
 * Return `"fresh" | "stale" | "expired"` for a manifest snapshot.
 *
 * @param {object} args
 * @param {string} args.lastServerSyncAt · ISO-8601 timestamp
 * @param {"C1"|"C2"|"C3"} args.dataClass · dominant class in the pack
 * @param {Date} [args.now] · injectable for tests
 */
export function computeFreshness({ lastServerSyncAt, dataClass, now } = {}) {
  if (dataClass === "C3") return "expired";
  if (!lastServerSyncAt) return "expired";
  const t = Date.parse(lastServerSyncAt);
  if (!Number.isFinite(t)) return "expired";
  const clock = (now instanceof Date ? now : new Date()).getTime();
  const age = Math.max(0, clock - t);
  const win = FRESHNESS_WINDOWS_MS[dataClass] || FRESHNESS_WINDOWS_MS.C2;
  if (age <= win.fresh) return "fresh";
  if (age <= win.stale) return "stale";
  return "expired";
}
